import { isConnectEnabled } from "../utils/connectVisibility";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { getSubdomain, getOrgPortalUrl, getMainAppUrl } from "../utils/subdomain";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../components/ui/dropdown-menu";
import { 
  LayoutDashboard, Trophy, Building, LogOut, Menu, X, Settings,
  DollarSign, MessageSquare, Calendar, Users, User, ChevronDown,
  Building2, Check, Loader2, ShieldAlert, ArrowLeft, MapPin, ClipboardList,
  UserCheck, Upload, Headphones, Mail, MessageCircle, Shield,
  BarChart3, Award, Store, RadioTower, Cable, Globe2, Megaphone
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FeatureGateProvider } from "../contexts/FeatureGateContext";
import AlertsDropdown from "./AlertsDropdown";
import AdminSearch from "./AdminSearch";
import ChatDropdown from "./ChatDropdown";
import SquadlineNotifications from "./SquadlineNotifications";
import { formatDistanceToNow } from "date-fns";
import SkipToContent from "./SkipToContent";
import BrandLogo from "./BrandLogo";
import EditorialPageHeader from "./EditorialPageHeader";
import { marketplaceAppIsInstalled } from "../utils/marketplaceInstallations";
const AdminLayout = ({ children, title, subtitle, actions, headerEyebrow }) => {
  const { user, token, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [roles, setRoles] = useState([]);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const [installedModules, setInstalledModules] = useState(() => new Set());

  const canAccess = (permission) => (
    user?.role !== "org_admin"
    || !Array.isArray(user?.admin_permissions)
    || user.admin_permissions.includes(permission)
  );

  useEffect(() => {
    if (token) {
      fetchPendingCount();
      fetchOrganization();
      fetchRoles();
      if (user?.role === "org_admin") fetchMarketplaceInstallations();
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== "org_admin") return undefined;
    const refresh = () => fetchMarketplaceInstallations();
    window.addEventListener("marketplace-installations-changed", refresh);
    return () => window.removeEventListener("marketplace-installations-changed", refresh);
  }, [token, user?.role]);

  const fetchMarketplaceInstallations = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/apps`, { headers: { Authorization: `Bearer ${token}` } });
      setInstalledModules(new Set((response.data.apps || []).filter(marketplaceAppIsInstalled).map((app) => app.slug)));
    } catch (error) {
      console.error("Failed to load installed modules", error);
      setInstalledModules(new Set());
    }
  };

  // Auto-switch org context when subdomain doesn't match user's current org_id
  useEffect(() => {
    const subdomain = getSubdomain();
    if (!subdomain || !token || !user) return;
    
    const syncOrgToSubdomain = async () => {
      try {
        // Look up the org for this subdomain
        const orgRes = await axios.get(`${API}/organizations/by-subdomain/${subdomain}`);
        const subdomainOrgId = orgRes.data?.org_id;
        
        if (subdomainOrgId && subdomainOrgId !== user.org_id) {
          // User's current org doesn't match subdomain — auto-switch
          console.log(`Subdomain org mismatch: switching from ${user.org_id} to ${subdomainOrgId} (${subdomain})`);
          await switchRole('org_admin', subdomainOrgId);
          // Reload org data after switch
          fetchOrganization();
        }
      } catch (error) {
        console.error('Failed to sync org context to subdomain:', error);
      }
    };
    
    syncOrgToSubdomain();
  }, [token, user?.org_id]);

  // Check if user is impersonating (has super_admin role but currently viewing as org_admin)
  useEffect(() => {
    if (roles.length > 0 && user) {
      const hasSuperAdminRole = roles.some(r => r.role === 'super_admin');
      const currentlyOrgAdmin = user.role === 'org_admin';
      setIsImpersonating(hasSuperAdminRole && currentlyOrgAdmin);
    }
  }, [roles, user]);

  const fetchPendingCount = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/financial-stats`, { headers });
      setPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error("Error fetching pending count:", error);
    }
  };

  const fetchOrganization = async () => {
    if (user?.org_id) {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/organizations/${user.org_id}`, { headers });
        const orgData = response.data;
        
        // If org has logo, fetch the image URLs separately
        if (orgData.has_logo || orgData.has_splash_image || orgData.has_sidebar_image) {
          try {
            const imagesResponse = await axios.get(`${API}/organizations/${user.org_id}/images`, { headers });
            orgData.logo_url = imagesResponse.data.logo_url;
            orgData.splash_image_url = imagesResponse.data.splash_image_url;
            orgData.sidebar_image_url = imagesResponse.data.sidebar_image_url;
          } catch (imgError) {
            console.error("Error fetching organization images:", imgError);
          }
        }
        
        setOrganization(orgData);
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/auth/my-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSwitchRole = async (role, orgId, subdomain) => {
    setSwitchingRole(true);
    try {
      await switchRole(role, orgId);
      if (role === 'parent') {
        navigate('/dashboard');
      } else if (role === 'org_admin') {
        // Redirect to the organization's subdomain if available
        if (subdomain) {
          const orgUrl = getOrgPortalUrl(subdomain);
          window.location.href = `${orgUrl}/admin`;
        } else {
          navigate('/admin');
          window.location.reload();
        }
      } else if (role === 'super_admin') {
        // Redirect to main app for super admin
        const mainUrl = getMainAppUrl();
        window.location.href = `${mainUrl}/admin`;
      }
    } catch (error) {
      console.error("Failed to switch role:", error);
    } finally {
      setSwitchingRole(false);
    }
  };

  const handleExitImpersonation = async () => {
    setExitingImpersonation(true);
    try {
      await switchRole('super_admin');
      toast.success("Returned to Super Admin view");
      // Redirect to main app URL
      const mainUrl = getMainAppUrl();
      window.location.href = `${mainUrl}/admin`;
    } catch (error) {
      console.error("Failed to exit impersonation:", error);
      toast.error("Failed to exit impersonation");
    } finally {
      setExitingImpersonation(false);
    }
  };

  // Grouped navigation sections
  const navSections = [
    // Main
    {
      items: [
        { icon: <LayoutDashboard className="w-5 h-5" />, label: "Today", path: "/admin" },
      ]
    },
    // Super Admin
    ...(user?.role === "super_admin" ? [{
      label: "Platform",
      items: [
        { icon: <Building className="w-5 h-5" />, label: "Organizations", path: "/admin/organizations" },
        { icon: <Users className="w-5 h-5" />, label: "Users", path: "/admin/users" },
      ]
    }, {
      label: "Marketing",
      items: [
        { icon: <Megaphone className="w-5 h-5" />, label: "Demo Funnel", path: "/admin/marketing/demo-funnel" },
      ]
    }] : []),
    // Programs group — org_admin only
    ...(user?.role === "org_admin" ? [{
      label: "Programs",
      items: [
        { icon: <ClipboardList className="w-5 h-5" />, label: "Programs", path: "/admin/programs", permission: "programs" },
        ...(installedModules.has("leagues") ? [{ icon: <Trophy className="h-5 w-5" />, label: "Leagues", path: "/admin/modules/leagues", permission: "teams" }] : []),
        ...(installedModules.has("tournaments") ? [{ icon: <Trophy className="h-5 w-5" />, label: "Tournaments", path: "/admin/modules/tournaments", permission: "teams" }] : []),
      ]
    }] : []),
    // Members
    {
      label: "Members",
      items: [
        { icon: <Users className="w-5 h-5" />, label: "People & Families", path: "/admin/people", permission: "registrations" },
      ]
    },
    // Operations
    {
      label: "Operations",
      items: [
        { icon: <Calendar className="w-5 h-5" />, label: "Scheduling", path: "/admin/scheduling", permission: "teams" },
        { icon: <Mail className="w-5 h-5" />, label: "Communication", path: "/admin/messaging", permission: "teams" },
        { icon: <Headphones className="w-5 h-5" />, label: "Support", path: "/admin/support", permission: "teams" },
      ]
    },
    // Finance
    {
      label: "Finance",
      items: [
        { icon: <DollarSign className="w-5 h-5" />, label: "Financials", path: "/admin/financials", permission: "payments", badge: pendingCount > 0 ? pendingCount : null },
      ]
    },
    // Insights
    ...(user?.role === "org_admin" && isConnectEnabled() ? [{
      items: [
        { icon: <Cable className="w-5 h-5" />, label: "Connect", path: "/admin/connect", permission: "reports" },
      ]
    }] : []),
    {
      label: "Insights",
      items: [
        { icon: <BarChart3 className="w-5 h-5" />, label: "Reports", path: "/admin/reports", permission: "reports" },
      ]
    },
    ...(user?.role === "org_admin" ? [{
      label: "Extend",
      items: [
        ...(installedModules.has("wstl") ? [{ icon: <RadioTower className="h-5 w-5" />, label: "WSTL Officiating", path: "/admin/wstl", permission: "teams" }] : []),
        ...(installedModules.has("website_pro") ? [{ icon: <Globe2 className="h-5 w-5" />, label: "Website", path: "/admin/website", permission: "settings" }] : []),
        { icon: <Store className="w-5 h-5" />, label: "Marketplace", path: "/admin/marketplace", permission: "settings" },
      ]
    }] : []),
  ].map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.permission || canAccess(item.permission)),
  })).filter((section) => section.items.length > 0);

  // Settings at bottom (separate from scrollable nav)
  const settingsItem = user?.role === "super_admin"
    ? { icon: <Settings className="w-5 h-5" />, label: "Settings", path: "/admin/platform" }
    : user?.role === "org_admin"
    ? (canAccess("settings") ? { icon: <Settings className="w-5 h-5" />, label: "Settings", path: "/admin/settings" } : null)
    : null;

  // Flat list for legacy references
  const navItems = navSections.flatMap(s => s.items);

  const isActive = (path) => {
    if (path === "/admin" && location.pathname === "/admin") {
      return true;
    }
    if (path !== "/admin" && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  // Get organizations from roles for dropdown
  const orgRoles = roles.filter(r => r.role === 'org_admin');

  return (
    <div className="admin-ui min-h-screen bg-[#f5f7f4]">
      <SkipToContent />
      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-50 lg:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 w-72 shadow-2xl flex flex-col bg-[#101a19]">
          {/* Sporthouse Logo at Top */}
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center justify-between">
              <BrandLogo surface="dark" className="h-10 w-auto" />
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-2xl hover:bg-white/10 transition-colors ml-2">
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>
          </div>

          {/* Organization Info */}
          <div className="px-5 py-4 border-b border-white/10">
            {organization ? (
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {organization.logo_url ? (
                  <img 
                    src={organization.logo_url} 
                    alt={organization.name} 
                    className="h-10 w-10 rounded-xl object-contain bg-white/10"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white truncate">{organization.name}</h2>
                  <span className="text-xs text-white/50">Organization workspace</span>
                </div>
              </div>
            ) : user?.role === 'super_admin' ? (
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-white/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white">Super Admin</h2>
                  <span className="text-xs text-white/50">Platform Control</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-white/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white">Admin Portal</h2>
                </div>
              </div>
            )}
          </div>
          
          <nav className="flex-1 px-3 py-2 overflow-y-auto" aria-label="Sporthouse admin navigation">
            {navSections.map((section, sIdx) => (
              <div key={sIdx} className={sIdx > 0 ? "mt-4" : ""}>
                {section.label && (
                  <p className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section.label}</p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-xl transition-all ${
                        isActive(item.path)
                          ? 'bg-white/20 text-white' 
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      aria-current={isActive(item.path) ? "page" : undefined}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {item.badge && (
                        <span className={`min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full flex items-center justify-center ${
                          isActive(item.path) ? 'bg-white text-slate-900' : 'bg-cyan-500 text-white'
                        }`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          
          {/* Bottom section with Settings + Sign Out */}
          <div className="border-t border-white/10">
            {settingsItem && (
              <div className="px-3 pt-3">
                <Link
                  to={settingsItem.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                    isActive(settingsItem.path)
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  aria-current={isActive(settingsItem.path) ? "page" : undefined}
                >
                  {settingsItem.icon}
                  <span className="text-sm font-medium">{settingsItem.label}</span>
                </Link>
              </div>
            )}
            <div className="p-4 pt-2">
              <Button 
                onClick={handleLogout} 
                variant="ghost" 
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 rounded-2xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
            <div className="px-4 pb-4">
              <div className="text-center">
                <span className="text-xs text-white/40">Powered by Sporthouse</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="admin-sidebar hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-72 lg:block lg:z-30 bg-[#101a19]">
        <div className="flex flex-col h-full">
          {/* Sporthouse Logo at Top - Full Width */}
          <div className="px-6 py-5 border-b border-white/10">
            <BrandLogo surface="dark" className="w-[205px]" />
          </div>

          {/* Organization Info */}
          <div className="px-5 py-4 border-b border-white/10">
            {organization ? (
              <div className="flex items-center gap-3">
                {organization.logo_url ? (
                  <img 
                    src={organization.logo_url} 
                    alt={organization.name} 
                    className="h-10 w-10 rounded-xl object-contain bg-white/10"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white/70" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white truncate">{organization.name}</h2>
                  <span className="text-xs text-white/50">Organization workspace</span>
                </div>
              </div>
            ) : user?.role === 'super_admin' ? (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white">Super Admin</h2>
                  <span className="text-xs text-white/50">Platform Control</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white">Admin Portal</h2>
                  <span className="text-xs text-white/50">Loading...</span>
                </div>
              </div>
            )}
          </div>
          
          <nav className="flex-1 px-3 py-2 overflow-y-auto" aria-label="Admin navigation">
            {navSections.map((section, sIdx) => (
              <div key={sIdx} className={sIdx > 0 ? "mt-4" : ""}>
                {section.label && (
                  <p className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section.label}</p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`admin-nav-item flex items-center justify-between px-4 py-2.5 rounded-xl transition-all ${
                        isActive(item.path)
                          ? 'active bg-white/[0.12] text-white shadow-sm'
                          : 'text-white/60 hover:bg-white/[0.07] hover:text-white'
                      }`}
                      data-testid={`admin-nav-${item.label.toLowerCase()}`}
                      aria-current={isActive(item.path) ? "page" : undefined}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {item.badge && (
                        <span className={`min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full flex items-center justify-center ${
                          isActive(item.path) ? 'bg-white text-slate-900' : 'bg-cyan-500 text-white'
                        }`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Bottom section — Settings + branding */}
          <div className="border-t border-white/10">
            {settingsItem && (
              <div className="px-3 pt-3">
                <Link
                  to={settingsItem.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                    isActive(settingsItem.path)
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid="admin-nav-settings"
                  aria-current={isActive(settingsItem.path) ? "page" : undefined}
                >
                  {settingsItem.icon}
                  <span className="text-sm font-medium">{settingsItem.label}</span>
                </Link>
              </div>
            )}
            <div className="p-4 pt-2">
              <div className="text-center">
                <span className="text-xs text-white/40">Powered by Sporthouse</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" tabIndex="-1" className="admin-main-content min-w-0">
        {/* Impersonation Banner */}
        {isImpersonating && organization && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5" />
              <span className="font-medium">
                You are viewing as <strong>{organization.name}</strong>
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExitImpersonation}
              disabled={exitingImpersonation}
              className="bg-white text-amber-600 hover:bg-amber-50 rounded-xl font-medium"
              data-testid="exit-impersonation-btn"
            >
              {exitingImpersonation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowLeft className="w-4 h-4 mr-2" />
              )}
              Exit Impersonation
            </Button>
          </div>
        )}

        {/* Top Bar */}
        <header className="admin-topbar sticky top-0 z-40 bg-[#f5f7f4]/90 backdrop-blur-xl border-b border-[#dce2dc]">
          <div className="flex items-center gap-4 px-5 py-3 lg:px-10">
            {/* Left side - Mobile menu button */}
            <button 
              className="p-2 rounded-2xl hover:bg-slate-100 transition-colors lg:hidden shrink-0" 
              onClick={() => setMobileMenuOpen(true)}
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>

            {/* Active organization context + universal search */}
            <div className="hidden xl:flex items-center gap-3 min-w-0">
              <div className="h-8 w-px bg-[#dce2dc]" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Working in</p>
                <p className="text-sm font-semibold text-[#101a19] truncate max-w-[190px]">
                  {organization?.name || (user?.role === 'super_admin' ? 'Sporthouse platform' : 'Organization')}
                </p>
              </div>
            </div>
            <div className="flex-1 hidden sm:block max-w-2xl">
              <AdminSearch />
            </div>

            {/* Right side - Chat, Alerts, Squadline, and User dropdown */}
            <div className="flex items-center gap-2 shrink-0 ml-auto">
              <ChatDropdown />
              {organization?.squadline_connected && (
                <SquadlineNotifications orgId={user?.org_id} />
              )}
              <AlertsDropdown />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 p-2 rounded-2xl hover:bg-slate-100 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{user?.name?.charAt(0) || 'A'}</span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-2xl">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* Profile */}
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin/profile')}
                    className="cursor-pointer rounded-lg"
                    data-testid="profile-menu-item"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>

                  {/* Coach Profile - Show if user is also a coach */}
                  {user?.coach_id && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer rounded-lg">
                        <Award className="w-4 h-4 mr-2" />
                        Coach
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="rounded-xl min-w-[200px]">
                        <DropdownMenuItem 
                          onClick={() => navigate('/coach/dashboard')}
                          className="cursor-pointer rounded-lg"
                          data-testid="coach-dashboard-link"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Coach Dashboard
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}

                  {/* Organizations - Nested */}
                  {(orgRoles.length > 0 || user?.role === 'super_admin') && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer rounded-lg">
                        <Building2 className="w-4 h-4 mr-2" />
                        Organizations
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="rounded-xl">
                        {user?.role === 'super_admin' && (
                          <DropdownMenuItem 
                            onClick={() => navigate('/admin/organizations')}
                            className="cursor-pointer rounded-lg"
                          >
                            <Building className="w-4 h-4 mr-2" />
                            All Organizations
                          </DropdownMenuItem>
                        )}
                        {orgRoles.length > 0 && user?.role === 'super_admin' && (
                          <DropdownMenuSeparator />
                        )}
                        {orgRoles.map((role) => (
                          <DropdownMenuItem
                            key={role.org_id}
                            onClick={() => handleSwitchRole('org_admin', role.org_id, role.subdomain)}
                            className="cursor-pointer rounded-lg flex items-center justify-between"
                            disabled={switchingRole}
                          >
                            <span className="truncate">{role.org_name || role.label}</span>
                            {role.is_current && <Check className="w-4 h-4 text-emerald-600 ml-2" />}
                          </DropdownMenuItem>
                        ))}
                        {orgRoles.length === 0 && user?.role !== 'super_admin' && (
                          <DropdownMenuItem disabled className="text-slate-400">
                            No organizations
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}

                  {/* Switch to Parent view if available */}
                  {roles.some(r => r.role === 'parent') && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleSwitchRole('parent')}
                        className="cursor-pointer rounded-lg"
                        disabled={switchingRole}
                      >
                        {switchingRole ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <User className="w-4 h-4 mr-2" />
                        )}
                        Switch to Parent View
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator />
                  
                  {/* Logout */}
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer rounded-lg text-red-600 focus:text-red-600"
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="p-5 lg:px-10 lg:py-8 max-w-[1600px] mx-auto">
          <FeatureGateProvider orgId={user?.org_id}>
            {/* Shared editorial page header */}
            {(title || subtitle || actions) && (
              title ? (
                <EditorialPageHeader
                  eyebrow={headerEyebrow || "Organization operations"}
                  title={title}
                  description={subtitle}
                  headingId="admin-page-title"
                  aside={actions}
                  className="mb-6"
                />
              ) : (
                <div className="mb-6 flex items-center justify-end gap-3">{actions}</div>
              )
            )}
            {children}
          </FeatureGateProvider>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
